import { isAddressNativeTokenPlaceholder } from "@repo/constants";
import type {
  ExecutionEngineClient,
  ExecutionEngineOperation,
} from "@repo/execution-engine-client";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { erc20Abi, parseUnits, type Hex } from "viem";
import {
  clearPortfolioRequestKey,
  isExecutionRequestConflictError,
  readExecutionRequestConflictOperationId,
  readOrCreatePortfolioRequestKey,
  toExecutionEvmAsset,
} from "~/features/execution";
import { getSigningDisabledMessage } from "~/lib/auth/app-auth";
import { getBrowserExecutionEngineClient } from "~/lib/execution-engine/browser-client";
import { m } from "~/lib/i18n";
import type { VmId } from "~/lib/auth/vm-types";
import { createUserActionMutationMeta } from "~/lib/user-actions";
import {
  useAccount,
  useChainId,
  useEstimateGas,
  useGasPrice,
  usePublicClient,
  useSendTransaction,
  useSwitchChain,
  useWriteContract,
} from "wagmi";

export interface DirectTokenDepositInput {
  amount: string;
  assetId: string;
  chainId: number;
  decimals: number;
  portfolioId: string;
  recipientAddress?: `0x${string}`;
  resume?: {
    readonly operationId: string;
    readonly txHash: Hex;
  };
  tokenAddress: `0x${string}`;
}

export interface DirectTokenDepositResult {
  operation: ExecutionEngineOperation;
  operationId: string;
  txHash: Hex;
}

export type DirectTokenDepositStage =
  | "idle"
  | "validating"
  | "creating_intent"
  | "waiting_wallet_signature"
  | "attaching_transaction"
  | "attached"
  | "attach_failed"
  | "failed";

export interface DirectTokenDepositIntentSnapshot {
  /**
   * The actual on-chain transfer amount in smallest units. For native tokens
   * this is the entered amount minus `nativeGasFeeReserveRaw`, so it equals the
   * value broadcast on-chain (not the gross amount the user typed).
   */
  readonly amountRaw: string;
  readonly assetId: string;
  readonly chainId: number;
  readonly decimals: number;
  readonly isNativeToken: boolean;
  /**
   * Native-gas buffer (smallest units) withheld from the transfer so the wallet
   * keeps enough native balance to pay for gas. Non-null only for native-token
   * deposits; lets the UI disclose that displayed transfer == on-chain transfer.
   */
  readonly nativeGasFeeReserveRaw: string | null;
  readonly operationId: string;
  readonly requestKey: string;
  readonly vaultAddress: `0x${string}`;
}

export interface DirectTokenDepositTransactionSnapshot extends DirectTokenDepositIntentSnapshot {
  readonly reusedStoredTxHash: boolean;
  readonly txHash: Hex;
}

export class DirectTokenDepositAttachError extends Error {
  readonly cause: unknown;
  readonly operationId: string;
  readonly txHash: Hex;

  constructor(params: {
    readonly cause: unknown;
    readonly operationId: string;
    readonly txHash: Hex;
  }) {
    const causeMessage = getErrorMessage(params.cause);
    super(
      `Deposit transaction was submitted, but tracking could not be connected yet. ${causeMessage}`,
    );
    this.name = "DirectTokenDepositAttachError";
    this.cause = params.cause;
    this.operationId = params.operationId;
    this.txHash = params.txHash;
  }
}

const DEPOSIT_ATTACH_RETRY_DELAYS_MS = [500, 1_500, 3_000] as const;
const DIRECT_DEPOSIT_TX_STORAGE_PREFIX = "glider:webapp-v2:direct-deposit-tx";

export interface UseDirectTokenDepositArgs {
  canSign: boolean;
  chainId: number | null;
  expectedSigner?: {
    readonly address: string;
    readonly vm: VmId;
  } | null;
  onIntentCreated?: (intent: DirectTokenDepositIntentSnapshot) => void;
  onStageChange?: (stage: DirectTokenDepositStage) => void;
  onTransactionSubmitted?: (
    transaction: DirectTokenDepositTransactionSnapshot,
  ) => void;
  recipientAddress: `0x${string}` | null;
}

function isValidTokenDecimals(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 255;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return m.deposit_error_transaction_failed();
}

function isNativeTokenAddress(address: `0x${string}`): boolean {
  return isAddressNativeTokenPlaceholder(address);
}

function normalizeDecimalAmountInput(amount: string): string {
  return amount.replace(/,/g, "").trim();
}

function evmAddressesEqual(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  return Boolean(left && right && left.toLowerCase() === right.toLowerCase());
}

function normalizeEvmSourceWalletAddress(address: string): `0x${string}` {
  const normalized = address.trim().toLowerCase();
  if (!/^0x[a-f0-9]{40}$/u.test(normalized)) {
    throw new Error(m.deposit_error_wallet_not_connected());
  }
  return normalized as `0x${string}`;
}

function waitForDepositAttachRetry(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

function readStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function directDepositStorageKey(fingerprintParts: readonly string[]): string {
  return [DIRECT_DEPOSIT_TX_STORAGE_PREFIX, ...fingerprintParts]
    .map((part) => encodeURIComponent(part))
    .join(":");
}

function readStoredDirectDepositTxHash(
  fingerprintParts: readonly string[],
): Hex | null {
  const stored = readStorage()?.getItem(
    directDepositStorageKey(fingerprintParts),
  );
  return stored && /^0x[a-fA-F0-9]{64}$/u.test(stored) ? (stored as Hex) : null;
}

function writeStoredDirectDepositTxHash(
  fingerprintParts: readonly string[],
  txHash: Hex,
): void {
  readStorage()?.setItem(directDepositStorageKey(fingerprintParts), txHash);
}

function clearStoredDirectDepositTxHash(
  fingerprintParts: readonly string[],
): void {
  readStorage()?.removeItem(directDepositStorageKey(fingerprintParts));
}

async function attachDepositTransactionWithRetry(params: {
  readonly client: ExecutionEngineClient;
  readonly operationId: string;
  readonly portfolioId: string;
  readonly txHash: Hex;
}): Promise<ExecutionEngineOperation> {
  let lastError: unknown = null;

  for (
    let attempt = 0;
    attempt <= DEPOSIT_ATTACH_RETRY_DELAYS_MS.length;
    attempt += 1
  ) {
    try {
      return await params.client.attachDepositTransaction(
        params.operationId,
        {
          txHash: params.txHash,
        },
        {
          capability: {
            portfolioIds: [params.portfolioId],
          },
        },
      );
    } catch (error) {
      lastError = error;
      const retryDelayMs = DEPOSIT_ATTACH_RETRY_DELAYS_MS[attempt];
      if (retryDelayMs === undefined) {
        break;
      }
      await waitForDepositAttachRetry(retryDelayMs);
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error("Failed to attach deposit transaction.");
}

export function useDirectTokenDeposit(input: UseDirectTokenDepositArgs) {
  const [stage, setStage] = useState<DirectTokenDepositStage>("idle");
  const { address, isConnected } = useAccount();
  const currentChainId = useChainId();
  const gasPriceQuery = useGasPrice({
    chainId: input.chainId ?? undefined,
    query: {
      enabled: input.chainId !== null,
    },
  });
  const publicClient = usePublicClient({
    chainId: input.chainId ?? undefined,
  });
  const estimateGasQuery = useEstimateGas({
    chainId: input.chainId ?? undefined,
    query: {
      enabled: Boolean(input.recipientAddress && input.chainId),
    },
    to: input.recipientAddress ?? "0x0000000000000000000000000000000000000000",
    value: parseUnits("0.00001", 18),
  });
  const nativeSend = useSendTransaction();
  const tokenSend = useWriteContract();
  const { switchChainAsync } = useSwitchChain();
  const setDepositStage = (nextStage: DirectTokenDepositStage): void => {
    setStage(nextStage);
    input.onStageChange?.(nextStage);
  };

  const mutation = useMutation<
    DirectTokenDepositResult,
    Error,
    DirectTokenDepositInput
  >({
    mutationFn: async (values) => {
      setDepositStage("validating");
      if (!input.canSign) {
        setDepositStage("failed");
        throw new Error(getSigningDisabledMessage("Direct deposit"));
      }

      if (!isConnected || !address) {
        setDepositStage("failed");
        throw new Error(m.deposit_error_wallet_not_connected());
      }

      if (input.expectedSigner) {
        if (input.expectedSigner.vm !== "evm") {
          setDepositStage("failed");
          throw new Error(m.deposit_error_unsupported_wallet());
        }
        if (!evmAddressesEqual(address, input.expectedSigner.address)) {
          setDepositStage("failed");
          throw new Error(m.deposit_error_wrong_wallet());
        }
      }

      if (!isValidTokenDecimals(values.decimals)) {
        setDepositStage("failed");
        throw new Error(m.deposit_error_asset_details_incomplete());
      }

      const recipientAddress =
        values.recipientAddress ?? input.recipientAddress;
      if (!recipientAddress) {
        setDepositStage("failed");
        throw new Error(m.deposit_error_destination_unavailable());
      }

      const portfolioId = values.portfolioId.trim();
      if (portfolioId.length === 0) {
        setDepositStage("failed");
        throw new Error(m.deposit_error_portfolio_not_ready());
      }

      const executionAsset = toExecutionEvmAsset({
        assetId: values.assetId,
        chainRef: String(values.chainId),
      });
      if (!executionAsset) {
        setDepositStage("failed");
        // Asset-specific: another asset may still work, so this must not read
        // as a service outage.
        throw new Error(m.deposit_error_asset_unsupported());
      }
      if (executionAsset.chainId !== values.chainId) {
        setDepositStage("failed");
        throw new Error(
          "Selected asset chain does not match the active deposit chain.",
        );
      }

      const client = getBrowserExecutionEngineClient({
        vmPreference: input.expectedSigner?.vm ?? "evm",
        walletAddress: input.expectedSigner?.address ?? address,
      });
      if (!client) {
        setDepositStage("failed");
        // Service-wide: retrying later is the only useful advice.
        throw new Error(m.deposit_error_temporarily_unavailable());
      }

      if (values.resume) {
        setDepositStage("attaching_transaction");
        let resumedOperation: ExecutionEngineOperation;
        try {
          resumedOperation = await attachDepositTransactionWithRetry({
            client,
            operationId: values.resume.operationId,
            portfolioId,
            txHash: values.resume.txHash,
          });
        } catch (error) {
          setDepositStage("attach_failed");
          throw new DirectTokenDepositAttachError({
            cause: error,
            operationId: values.resume.operationId,
            txHash: values.resume.txHash,
          });
        }

        setDepositStage("attached");
        return {
          operation: resumedOperation,
          operationId: resumedOperation.operationId,
          txHash: values.resume.txHash,
        };
      }

      if (currentChainId !== values.chainId) {
        await switchChainAsync({ chainId: values.chainId });
      }

      let sourceWalletAddress: `0x${string}`;
      try {
        sourceWalletAddress = normalizeEvmSourceWalletAddress(
          input.expectedSigner?.address ?? address,
        );
      } catch (error) {
        setDepositStage("failed");
        throw error;
      }
      const normalizedAmount = normalizeDecimalAmountInput(values.amount);
      let transferAmountSmallestUnit = parseUnits(
        normalizedAmount,
        values.decimals,
      );
      const isNative = isNativeTokenAddress(values.tokenAddress);
      // Native-gas buffer (smallest units) withheld from the transfer so the
      // wallet keeps enough native balance to cover gas. Surfaced on the intent
      // snapshot so the UI can disclose that the on-chain transfer amount is
      // (entered − reserve), keeping displayed == on-chain.
      let nativeGasFeeReserveSmallestUnit = 0n;

      if (isNative) {
        let gasPrice = gasPriceQuery.data;
        let gasLimit = estimateGasQuery.data;
        // Existing hosts prefetch against their known destination. Only the
        // deferred builder path supplies the recipient at submit time and needs
        // a mutation-time probe; keep this extra RPC off every other host.
        if ((!gasPrice || !gasLimit) && values.recipientAddress) {
          try {
            gasPrice ??= await publicClient?.getGasPrice();
            gasLimit ??= await publicClient?.estimateGas({
              account: sourceWalletAddress,
              to: recipientAddress,
              // Estimating a zero-value native transfer obtains the same base
              // gas limit without failing merely because a small wallet cannot
              // afford an arbitrary probe amount.
              value: 0n,
            });
          } catch {
            setDepositStage("failed");
            throw new Error(
              "Could not estimate network fee for native token deposit.",
            );
          }
        }
        if (!gasPrice || !gasLimit) {
          setDepositStage("failed");
          throw new Error(
            "Could not estimate network fee for native token deposit.",
          );
        }

        const SAFETY_MULTIPLIER = 3n;
        const feeBudget = gasLimit * gasPrice * SAFETY_MULTIPLIER;
        if (transferAmountSmallestUnit <= feeBudget) {
          setDepositStage("failed");
          throw new Error(m.deposit_error_amount_below_network_fees());
        }

        transferAmountSmallestUnit -= feeBudget;
        nativeGasFeeReserveSmallestUnit = feeBudget;
      }

      if (transferAmountSmallestUnit <= 0n) {
        setDepositStage("failed");
        throw new Error(m.deposit_error_amount_must_be_positive());
      }

      const fingerprintParts = [
        "direct",
        "source",
        "evm",
        sourceWalletAddress,
        portfolioId,
        executionAsset.assetId,
        executionAsset.chainId.toString(),
        transferAmountSmallestUnit.toString(),
        recipientAddress.toLowerCase(),
      ];
      const requestKey = readOrCreatePortfolioRequestKey({
        action: "deposit",
        portfolioId,
        fingerprintParts,
      });
      setDepositStage("creating_intent");
      let operationId: string;
      try {
        const intent = await client.createDepositIntent({
          requestKey,
          portfolioId,
          chainId: executionAsset.chainId,
          assetId: executionAsset.assetId,
          amount: transferAmountSmallestUnit.toString(),
          vaultAddress: recipientAddress,
        });
        operationId = intent.operationId;
      } catch (error) {
        if (!isExecutionRequestConflictError(error)) {
          throw error;
        }

        // A stale request key (from a lost createDepositIntent response) now
        // points at an operation the engine fingerprinted with different
        // inputs. Clear the key so the next attempt mints a fresh one instead
        // of replaying the same 409 for the request-key TTL, then adopt the
        // conflicting operation and attach this transfer to it rather than
        // registering a second intent. The intent request runs before any
        // wallet broadcast, so adopting here cannot double-submit funds; the
        // stored tx-hash guard still prevents a re-broadcast on later retries.
        clearPortfolioRequestKey({
          action: "deposit",
          portfolioId,
          fingerprintParts,
        });

        const conflictOperationId =
          readExecutionRequestConflictOperationId(error);
        if (!conflictOperationId) {
          throw new Error(
            "An earlier deposit request is still registered for this account. Try the deposit again to submit a fresh request.",
          );
        }

        try {
          const adoptedOperation =
            await client.getOperation(conflictOperationId);
          operationId = adoptedOperation.operationId;
        } catch {
          throw new Error(
            "An earlier deposit request is still registered for this account. Try the deposit again to submit a fresh request.",
          );
        }
      }
      const intentSnapshot: DirectTokenDepositIntentSnapshot = {
        amountRaw: transferAmountSmallestUnit.toString(),
        assetId: executionAsset.assetId,
        chainId: executionAsset.chainId,
        decimals: values.decimals,
        isNativeToken: isNative,
        nativeGasFeeReserveRaw:
          nativeGasFeeReserveSmallestUnit > 0n
            ? nativeGasFeeReserveSmallestUnit.toString()
            : null,
        operationId,
        requestKey,
        vaultAddress: recipientAddress,
      };
      await input.onIntentCreated?.(intentSnapshot);

      const storedTxHash = readStoredDirectDepositTxHash(fingerprintParts);
      if (!storedTxHash) {
        setDepositStage("waiting_wallet_signature");
      }
      const txHash =
        storedTxHash ??
        (isNative
          ? await nativeSend.sendTransactionAsync({
              chainId: values.chainId,
              to: recipientAddress,
              value: transferAmountSmallestUnit,
            })
          : await tokenSend.writeContractAsync({
              abi: erc20Abi,
              address: values.tokenAddress,
              args: [recipientAddress, transferAmountSmallestUnit],
              chainId: values.chainId,
              functionName: "transfer",
            }));

      if (!storedTxHash) {
        writeStoredDirectDepositTxHash(fingerprintParts, txHash);
      }
      const transactionSnapshot: DirectTokenDepositTransactionSnapshot = {
        ...intentSnapshot,
        reusedStoredTxHash: storedTxHash !== null,
        txHash,
      };
      await input.onTransactionSubmitted?.(transactionSnapshot);

      setDepositStage("attaching_transaction");
      let operation: ExecutionEngineOperation;
      try {
        operation = await attachDepositTransactionWithRetry({
          client,
          operationId,
          portfolioId,
          txHash,
        });
      } catch (error) {
        setDepositStage("attach_failed");
        throw new DirectTokenDepositAttachError({
          cause: error,
          operationId,
          txHash,
        });
      }

      clearPortfolioRequestKey({
        action: "deposit",
        portfolioId,
        fingerprintParts,
      });
      clearStoredDirectDepositTxHash(fingerprintParts);
      setDepositStage("attached");

      return {
        operation,
        operationId: operation.operationId,
        txHash,
      };
    },
    onError: (error) => {
      setDepositStage(
        error instanceof DirectTokenDepositAttachError
          ? "attach_failed"
          : "failed",
      );
    },
    retry: false,
    throwOnError: false,
    meta: createUserActionMutationMeta(
      "portfolio.deposit.execute",
      "portfolio",
      { onErrorMessage: getErrorMessage },
    ),
  });

  return {
    ...mutation,
    stage,
  };
}
